/**
 * 凭证 builder 公共工具
 *
 * 设计来源：doc/反恐舱单优化体验/设计文档_工具化体验优化.md §7.2
 * - 凭证正文为固定英文（与海关申报口径一致），本文件不做国际化
 * - 取不到即 '—'，并汇总进 missing（可选字段不计入）
 * - 字典键约定见下方 dict* 辅助函数（由宿主侧按 search-batch-edi 结果填充）
 */
import type {
  CertificateDict,
  CertificateField,
  CertificateSection,
} from '../types'

/** 舱单详情 / 回执 / 日志等原始对象 */
export type CertRow = Record<string, unknown>

/** 缺失占位符 */
export const DASH = '—'

/** 转字符串：undefined / null → ''；字符串去首尾空格 */
export function text(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

/** 按候选字段取第一个非空值 */
export function pick(row: CertRow | null | undefined, ...keys: string[]): string {
  if (!row) return ''
  for (const key of keys) {
    const value = text(row[key])
    if (value) return value
  }
  return ''
}

/** 提单号展示：SCAC / Carrier Code 前缀 + 空格 + 提单号（SCAC 为空时原样输出提单号） */
export function billWithScac(scac: unknown, bill: unknown): string {
  const billText = text(bill)
  if (!billText) return ''
  const scacText = text(scac)
  return scacText ? `${scacText} ${billText}` : billText
}

/** 日期时间展示：去掉 T 分隔符与毫秒 */
export function formatDateTime(value: unknown): string {
  const raw = text(value)
  if (!raw) return ''
  return raw.replace('T', ' ').replace(/\.\d+$/, '')
}

/** 布尔 → Yes / No（未知 → ''） */
export function yesNo(value: unknown): string {
  if (value === true || value === 1 || value === '1') return 'Yes'
  if (value === false || value === 0 || value === '0') return 'No'
  return ''
}

/** 箱属（SOC→Yes / COC→No），兼容布尔 */
export function socLabel(value: unknown): string {
  const raw = text(value).toUpperCase()
  if (raw === 'SOC') return 'Yes'
  if (raw === 'COC') return 'No'
  return yesNo(value)
}

/** 箱号串拆分为数组（逗号 / 分号 / 空白分隔） */
export function parseContainerNos(value: unknown): string[] {
  const raw = text(value)
  if (!raw) return []
  return raw
    .split(/[,，;；\s]+/)
    .map(item => item.trim())
    .filter(Boolean)
}

/** 取子列表行 */
export function rowsOf(detail: CertRow, key: string): CertRow[] {
  const list = detail[key]
  if (!Array.isArray(list)) return []
  return list.filter((row): row is CertRow => !!row && typeof row === 'object')
}

/** 按角色取参与方（role 大小写不敏感） */
export function findParty(detail: CertRow, role: string): CertRow | undefined {
  return rowsOf(detail, 'addressList').find(row => text(row.role).toUpperCase() === role)
}

/** 取该箱对应的货物行（containerNos / containerNoList 任一命中） */
export function cargoRowsForContainer(detail: CertRow, containerNo: string): CertRow[] {
  const target = text(containerNo).toUpperCase()
  if (!target) return []
  return rowsOf(detail, 'cargoList').filter(row => {
    const nos = parseContainerNos(row.containerNos)
    if (nos.length) return nos.some(no => no.toUpperCase() === target)
    const list = Array.isArray(row.containerNoList) ? row.containerNoList : []
    return list.some(no => text(no).toUpperCase() === target)
  })
}

/** 求和（仅统计该字段存在的行；无有效值时返回 ''） */
export function sumOf(rows: CertRow[], key: string): string {
  let total = 0
  let found = false
  for (const row of rows) {
    const raw = row[key]
    const value = typeof raw === 'number' ? raw : Number(text(raw))
    if (Number.isFinite(value)) {
      total += value
      found = true
    }
  }
  return found ? String(total) : ''
}

/** 多条货物行的「数量 + 单位」并列展示（100 CTN + 5 PLT，不相加） */
export function qtyWithUnit(rows: CertRow[], dict?: CertificateDict): string {
  return rows
    .map(row => {
      const qty = text(row.quantity)
      const unit = dictPackingUnit(dict, row.unitTypeId)
      return [qty, unit].filter(Boolean).join(' ')
    })
    .filter(Boolean)
    .join(' + ')
}

/** 同一箱内多货行品名（'; ' 拼接，不跨箱） */
export function goodsDescription(rows: CertRow[]): string {
  return rows
    .map(row => text(row.cargoName))
    .filter(Boolean)
    .join('; ')
}

/** 部分产品（ISF/ACI）的税号类字段：类型 + 号码 */
export function codeWithType(type: unknown, number: unknown): string {
  return [text(type), text(number)].filter(Boolean).join(' ')
}

// ─── 字典读取（键由宿主侧按 search-batch-edi 结果写入）────────────────

function dictLookup(dict: CertificateDict | undefined, prefix: string, id: unknown): string {
  const key = text(id)
  if (!dict || !key) return ''
  return text(dict[`${prefix}:${key}`])
}

/** 港口 EDI 代码（无则 UN/LOCODE） */
export function dictPort(dict: CertificateDict | undefined, id: unknown): string {
  return dictLookup(dict, 'seaport', id)
}

/** 港口英文名（ICS2 运输区块使用） */
export function dictPortName(dict: CertificateDict | undefined, id: unknown): string {
  return dictLookup(dict, 'seaportName', id)
}

/** 港口所属国家 2 位码（UN/LOCODE 前两位） */
export function dictPortCountry(dict: CertificateDict | undefined, id: unknown): string {
  return dictLookup(dict, 'seaportCountry', id)
}

/** 国家 2 位代码 */
export function dictCountry(dict: CertificateDict | undefined, id: unknown): string {
  return dictLookup(dict, 'country', id)
}

/** 箱型代码 */
export function dictContainerType(dict: CertificateDict | undefined, id: unknown): string {
  return dictLookup(dict, 'containerType', id)
}

/** 包装单位代码 */
export function dictPackingUnit(dict: CertificateDict | undefined, id: unknown): string {
  return dictLookup(dict, 'packingUnit', id)
}

/** 承运人 9000 Code（EMF CLOSE 的 CARRIER CODE） */
export function dictCarrierCode9000(dict: CertificateDict | undefined, id: unknown): string {
  return dictLookup(dict, 'carrierCode9000', id)
}

/** 承运人 EORI（ICS2） */
export function dictCarrierEori(dict: CertificateDict | undefined, id: unknown): string {
  return dictLookup(dict, 'carrierEori', id)
}

/** 申报联系人 EORI（ICS2） */
export function dictContactEori(dict: CertificateDict | undefined, id: unknown): string {
  return dictLookup(dict, 'contactEori', id)
}

// ─── 区块构建 ────────────────────────────────────────────────────────

/** 键值网格区块 */
export function gridSection(
  columns: number,
  fields: CertificateField[],
  title?: string
): CertificateSection {
  return title
    ? { kind: 'grid', columns, title, fields }
    : { kind: 'grid', columns, fields }
}

/** 表格区块：无数据时输出一行占位并记入 missing */
export function tableSection(
  title: string,
  headers: string[],
  rows: string[][],
  missing: string[]
): CertificateSection {
  if (!rows.length) {
    missing.push(title)
    return { kind: 'table', title, table: { headers, rows: [headers.map(() => DASH)] } }
  }
  rows.forEach(row => {
    row.forEach((cell, index) => {
      if (cell === DASH) missing.push(`${title} / ${headers[index]}`)
    })
  })
  return { kind: 'table', title, table: { headers, rows } }
}

/**
 * FILING STATUS 展示：code + 英文描述。
 * - 描述为空或与 code 完全相同 → 仅 code
 * - 描述已原样包含 code（如 ISF 的「S1 - B/L Matched」、AMS 的「Matched（1Y）」）→ 仅描述，避免重复
 * - 否则 → "CODE (Description)"（含 code 与描述的纯大小写差异，如「ACCEPTED (Accepted)」）
 */
export function filingStatus(
  code: unknown,
  resolve?: (code: string, sendType?: string) => string,
  sendType?: unknown
): string {
  const raw = text(code)
  if (!raw) return ''
  const desc = text(resolve?.(raw, text(sendType)))
  if (!desc || desc === raw) return raw
  if (desc.includes(raw)) return desc
  return `${raw} (${desc})`
}

/** LOGS CODE 列：回执状态优先，缺失回退发送结果 */
function logCodeOf(row: CertRow): string {
  const status = pick(row, 'receiptStatus')
  if (status) return status
  if (row.sendRespStatus === 1) return 'SUCCESS'
  if (row.sendRespStatus === 0) return 'FAILED'
  return ''
}

/** LOGS 区块：回执列表（manifestReceipt/getList）→ EVENT TIME / CODE / MESSAGE */
export function logsSection(
  receipts: unknown[] | undefined,
  missing: string[]
): CertificateSection {
  const rows = (receipts ?? [])
    .filter((row): row is CertRow => !!row && typeof row === 'object')
    .map(row => [
      formatDateTime(
        pick(row, 'receiptTime') ||
          pick(row, 'logTime') ||
          pick(row, 'sendTime') ||
          pick(row, 'createdTime')
      ) || DASH,
      logCodeOf(row) || DASH,
      pick(row, 'receiptMessage') || pick(row, 'message') || DASH,
    ])
  return tableSection('LOGS', ['EVENT TIME', 'CODE', 'MESSAGE'], rows, missing)
}

/** 字段收集器：空值输出 '—' 并按需记入 missing */
export class FieldCollector {
  readonly fields: CertificateField[] = []

  readonly missing: string[]

  constructor(missing: string[]) {
    this.missing = missing
  }

  /** 必填字段：空值 → '—' 并记入 missing */
  add(label: string, value: unknown): this {
    const rendered = text(value)
    if (rendered) {
      this.fields.push({ label, value: rendered })
    } else {
      this.fields.push({ label, value: DASH })
      this.missing.push(label)
    }
    return this
  }

  /** 可选字段：空值 → '—'，不记入 missing */
  optional(label: string, value: unknown): this {
    this.fields.push({ label, value: text(value) || DASH })
    return this
  }
}

/** 参与方地址块：eori → title → address1 → address2 → city → country → postalCode → state → contact */
export function partyAddress(
  row: CertRow | undefined,
  options: { dict?: CertificateDict; contact?: boolean } = {}
): string {
  if (!row) return ''
  const lines: string[] = []
  const push = (value: string) => {
    if (value) lines.push(value)
  }
  push(text(row.eori))
  push(text(row.title))
  push(text(row.address1))
  push(text(row.address2))
  push(text(row.city))
  push(dictCountry(options.dict, row.countryId))
  push(text(row.postalCode))
  push(text(row.stateCode) || text(row.state))
  if (options.contact) push(text(row.contactName) ? `Contact: ${text(row.contactName)}` : '')
  return lines.join('\n')
}

/** 取最新一条回执（按 receiptTime 倒序，缺失时取首条） */
export function latestReceipt(receipts?: unknown[]): CertRow | undefined {
  const rows = (receipts ?? []).filter(
    (row): row is CertRow => !!row && typeof row === 'object'
  )
  if (!rows.length) return undefined
  return [...rows].sort((a, b) =>
    text(b.receiptTime).localeCompare(text(a.receiptTime))
  )[0]
}

/** 回执字段取值（最新回执优先） */
export function receiptValue(receipts: unknown[] | undefined, key: string): string {
  return pick(latestReceipt(receipts), key)
}

/** 去重 missing 列表 */
export function dedupeMissing(missing: string[]): string[] {
  return Array.from(new Set(missing))
}
